import type {
    EmailImportPaperRow,
    MatchedTopicRef,
} from './paperInboxViewModel';

export interface PaperInboxApiMatchedTopic {
    id: string;
    name: string;
}

export interface PaperInboxApiCandidate extends Omit<EmailImportPaperRow, 'matchedTopics'> {
    matched_topics?: PaperInboxApiMatchedTopic[];
}

export interface PaperInboxFallbackTopicMembershipRow {
    id: string;
    paper_id: string | null;
    project: {
        id: string;
        name: string;
    };
}

export function normalizeMatchedTopics(
    topics: Array<PaperInboxApiMatchedTopic | MatchedTopicRef> | undefined
): MatchedTopicRef[] {
    const seen = new Set<string>();
    const normalized: MatchedTopicRef[] = [];

    for (const topic of topics || []) {
        const id = String(topic.id || '').trim();
        const name = String(topic.name || '').trim();

        if (!id || !name || seen.has(id)) {
            continue;
        }

        seen.add(id);
        normalized.push({ id, name });
    }

    return normalized;
}

export function buildMatchedTopicsByPaper(
    fallbackTopicRows: PaperInboxFallbackTopicMembershipRow[]
): Map<string, MatchedTopicRef[]> {
    const matchedTopicsByPaper = new Map<string, MatchedTopicRef[]>();

    for (const row of fallbackTopicRows) {
        if (!row.paper_id) {
            continue;
        }

        const current = matchedTopicsByPaper.get(row.paper_id) ?? [];
        current.push({
            id: row.project.id,
            name: row.project.name,
        });
        matchedTopicsByPaper.set(row.paper_id, normalizeMatchedTopics(current));
    }

    return matchedTopicsByPaper;
}

export function hydrateCandidatesWithMatchedTopics(
    candidates: PaperInboxApiCandidate[],
    matchedTopicsByPaper: Map<string, MatchedTopicRef[]>
): EmailImportPaperRow[] {
    return candidates.map((candidate) => {
        const hasAuthoritativeApiMatches = candidate.matched_topics !== undefined;
        const apiMatchedTopics = normalizeMatchedTopics(candidate.matched_topics);
        const localMatchedTopics = candidate.paper_id
            ? (matchedTopicsByPaper.get(candidate.paper_id) ?? [])
            : [];

        return {
            ...candidate,
            matchedTopics: hasAuthoritativeApiMatches ? apiMatchedTopics : localMatchedTopics,
        };
    });
}

export function resolveActiveTopicCount(
    apiActiveTopicCount: number | undefined,
    fallbackTopicCount: number
): number {
    return apiActiveTopicCount ?? fallbackTopicCount;
}
